import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { keyedPath } from '@/shared/api/keyedPath';
import { useAuthStore } from '@/shared/auth/store';
import { usePrefState } from '@/shared/prefs/usePrefState';
import { readPref, removePref, writePref } from '@/shared/prefs/storage';

/**
 * slice-4474 (John 28 Oct) — the user's GLOBAL "Auto-hide results after search (default)" setting,
 * from the profile menu. It is the default of every screen's "Auto-hide after search" tick
 * (slice-3671, dev-rules §4.25); a tick the user changes on one screen is still remembered for that
 * screen; changing the global setting CLEARS every per-screen choice so it really applies
 * everywhere.
 *
 * <p><b>Where it lives.</b> The setting itself is server-side, in the user's PH10 settings
 * (dbxx.ph10_usr_setting via /api/users/settings — the same store as Search Exact Match, ADR-0078),
 * so it follows the user to any PC. Its value is JSON {@code {"on":true,"at":"<ISO time>"}}: the
 * time stamps WHEN it was last changed. The per-screen ticks stay in this browser (usePrefState,
 * keyed by userId). Every browser keeps a local mirror of the global value plus the stamp it last
 * applied; when the server's stamp differs (the user changed the setting here or on another PC),
 * this browser drops its per-screen ticks and adopts the new default. useCriteriaCollapse reads
 * only the local mirror, so a screen never waits on a request.
 */
export const AUTO_HIDE_SETTING_CATEGORY = 'Window';
export const AUTO_HIDE_SETTING_KEY = 'AutoHideResults';
/** Local mirror of the global default (JSON boolean). */
export const AUTO_HIDE_DEFAULT_PREF = 'criteria.autoHide.default';
/** The server stamp this browser last applied (cleared its per-screen ticks for). */
export const AUTO_HIDE_APPLIED_PREF = 'criteria.autoHide.appliedAt';
/** Per-screen tick keys start with this (useCriteriaCollapse.autoHidePrefKey). */
export const AUTO_HIDE_SCREEN_PREFIX = 'criteria.autoHide:';

export interface AutoHideGlobal {
  on: boolean;
  at: string;
}

export function parseAutoHideSetting(raw: string | null | undefined): AutoHideGlobal | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<AutoHideGlobal>;
    if (typeof v.on === 'boolean' && typeof v.at === 'string' && v.at !== '') return { on: v.on, at: v.at };
  } catch {
    /* not ours — ignore */
  }
  return null;
}

/** Remove every per-screen Auto-hide tick this user has in this browser. Returns how many. */
export function clearPerScreenAutoHide(userId: string): number {
  if (!userId) return 0;
  const prefix = `ph:${userId}:${AUTO_HIDE_SCREEN_PREFIX}`;
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k.slice(`ph:${userId}:`.length));
    }
  } catch {
    return 0;
  }
  for (const k of keys) removePref(userId, k);
  return keys.length;
}

/**
 * Bring this browser in line with the global setting: a stamp it has not applied yet clears the
 * per-screen ticks first, then the mirror takes the value.
 */
export function applyAutoHideGlobal(userId: string, g: AutoHideGlobal): void {
  if (!userId) return;
  if (readPref<string | null>(userId, AUTO_HIDE_APPLIED_PREF, null) !== g.at) {
    clearPerScreenAutoHide(userId);
    writePref(userId, AUTO_HIDE_APPLIED_PREF, g.at);
  }
  if (readPref<boolean | null>(userId, AUTO_HIDE_DEFAULT_PREF, null) !== g.on) {
    writePref(userId, AUTO_HIDE_DEFAULT_PREF, g.on);
  }
}

const settingKey = ['settings', 'effective', AUTO_HIDE_SETTING_CATEGORY, AUTO_HIDE_SETTING_KEY, null] as const;
const SETTING_IDS = { category: AUTO_HIDE_SETTING_CATEGORY, key: AUTO_HIDE_SETTING_KEY };
const settingPath = () => keyedPath('/api/users/settings/{category}/{key}', SETTING_IDS);
const effectivePath = () => keyedPath('/api/users/settings/{category}/{key}/effective', SETTING_IDS);

/**
 * The global setting for the profile menu, and the SYNC: mounted once in the app header
 * (ProfileMenu), it reads the server value at sign-in and applies it to this browser.
 */
export function useAutoHideGlobal() {
  const userId = useAuthStore((s) => s.session?.userId ?? '');
  const qc = useQueryClient();
  const [mirror] = usePrefState<boolean>(AUTO_HIDE_DEFAULT_PREF, true);
  const q = useQuery<AutoHideGlobal | null>({
    queryKey: [...settingKey, userId],
    queryFn: async () => {
      try {
        const res = await api<{ value: string | null }>(effectivePath());
        return parseAutoHideSetting(res?.value ?? null);
      } catch (e) {
        if (e instanceof Error && 'status' in e && (e as { status?: number }).status === 404) return null;
        throw e;
      }
    },
    enabled: userId !== '',
    staleTime: 60_000,
    retry: false,
  });
  useEffect(() => {
    if (q.data) applyAutoHideGlobal(userId, q.data);
  }, [q.data, userId]);

  const save = useMutation({
    mutationFn: (g: AutoHideGlobal) =>
      api<unknown>(settingPath(), { method: 'PUT', json: { value: JSON.stringify(g) } }),
    onSuccess: (_r, g) => qc.setQueryData([...settingKey, userId], g),
  });

  return {
    /** The effective default: the server value once read, else this browser's mirror (On). */
    on: q.data?.on ?? mirror,
    saving: save.isPending,
    error: save.error instanceof Error ? save.error.message : null,
    setOn: (on: boolean) => {
      const g = { on, at: new Date().toISOString() };
      // Applied here at once (the screens read the mirror), then saved for the user's other PCs.
      applyAutoHideGlobal(userId, g);
      save.mutate(g);
    },
  };
}
